import { Alert, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import ModalBase from "components/shared/ModalBase";
import StyledButton from "components/shared/StyledButton";
import { StyledMenuItem, StyledSelect } from "components/shared/StyledSelect";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import axios from "axios";
import { formatPayloadMegabytes, payloadTooLargeDetailsFromError } from "helpers/jsonPayloadLimit";
import { useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { ETransferTermStatus } from "store/transferTermsStore";

interface ImportResult {
    total: number;
    createdCount: number;
    deduplicatedCount?: number;
}

interface ValidationErrors {
    missingTransferees: string[];
    missingIsbns: string[];
    rowErrors: { row: number; ean13?: string; transferee?: string; reason: string }[];
}

interface Props {
    open: boolean;
    onClose: () => void;
    onImported: () => void;
}

/**
 * Kept in sync with MAX_ROWS_PER_IMPORT in TransferTermController.adminBulkImport.
 * Purely informational client-side — the backend is the source of truth.
 */
const MAX_ROWS_PER_IMPORT = 150;

/**
 * CSV headers are printed literally (French) since that's what the source data uses.
 * The backend parser tolerates case, accents, extra spaces and English equivalents.
 */
const REQUIRED_COLUMNS = [
    { header: "ISBN", descriptionKey: "ISBN", example: "9782898085611" },
    { header: "Cessionnaire", descriptionKey: "Transferee", example: "Éditions J'ai Lu" },
    { header: "Date début", descriptionKey: "StartDate", example: "2024-11-08" },
    { header: "Date fin", descriptionKey: "EndDate", example: "2029-11-08" },
    { header: "Fréquence de reddition", descriptionKey: "PeriodDuration", example: "6" },
    { header: "Délai de paiement", descriptionKey: "PaymentDelay", example: "120" },
] as const;

const OPTIONAL_COLUMNS = [
    { header: "Statut", descriptionKey: "Status", example: "UP_TO_DATE" },
    { header: "Codes pays", descriptionKey: "CountryCodes", example: "FR,BE" },
    { header: "Référence", descriptionKey: "Reference", example: "CDR-2024-001" },
] as const;

/**
 * Statuses selectable at creation. Matches the manual form (SettingStep) which offers only
 * these two. TO_SIGN, EXPIRES_SOON, EXPIRED are computed by crons or by the agreement flow.
 */
const STATUS_OPTION_KEYS: { value: ETransferTermStatus; key: "UpToDate" | "UnderNegotiation" }[] = [
    { value: ETransferTermStatus.UP_TO_DATE, key: "UpToDate" },
    { value: ETransferTermStatus.UNDER_NEGOTIATION, key: "UnderNegotiation" },
];

/**
 * Controls the first payment tracked in transfer_term_sales:
 * - `closest` mirrors the manual form default (nearest scheduled payment to today).
 * - `start` anchors at the contract start, materialising every historical payment date.
 */
type TrackingStart = "closest" | "start";
const TRACKING_START_OPTION_KEYS: { value: TrackingStart; key: "Closest" | "Start" }[] = [
    { value: "closest", key: "Closest" },
    { value: "start", key: "Start" },
];

/**
 * Admin-only bulk import of transfer terms (cessions) from a CSV.
 *
 * The admin must first switch to the target company via the command palette;
 * the endpoint uses the active company from the JWT.
 */
const AdminImportModal = ({ open, onClose, onImported }: Props) => {
    const [t, i18n] = useTranslation();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [file, setFile] = useState<File | null>(null);
    const [status, setStatus] = useState<ETransferTermStatus>(ETransferTermStatus.UP_TO_DATE);
    const [trackingStart, setTrackingStart] = useState<TrackingStart>("closest");
    const [countryCodes, setCountryCodes] = useState<string>("");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<ImportResult | null>(null);
    const [fileError, setFileError] = useState<string | null>(null);
    const [validationErrors, setValidationErrors] = useState<ValidationErrors | null>(null);

    const resetAndClose = () => {
        setFile(null);
        setStatus(ETransferTermStatus.UP_TO_DATE);
        setTrackingStart("closest");
        setCountryCodes("");
        setResult(null);
        setFileError(null);
        setValidationErrors(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
        onClose();
    };

    const submit = async () => {
        if (!file) {
            toast.error(t("TransferTerms.AdminImport.Toast.FileRequired") as string);
            return;
        }

        const parsedCountryCodes = countryCodes
            .split(",")
            .map((c) => c.trim().toUpperCase())
            .filter(Boolean);

        const form = new FormData();
        form.append("file", file);
        form.append("status", String(status));
        form.append("trackingStart", trackingStart);
        if (parsedCountryCodes.length) {
            form.append("countryCodes", JSON.stringify(parsedCountryCodes));
        }

        setLoading(true);
        setFileError(null);
        setValidationErrors(null);
        try {
            const resp = await axios.post<{ data: ImportResult }>(
                import.meta.env.REACT_APP_API_ENDPOINT + "/transfer-term/admin-bulk-import",
                form,
                { headers: { "Content-Type": "multipart/form-data" } },
            );
            setResult(resp.data.data);
            if (resp.data.data.createdCount > 0) {
                toast.success(
                    t("TransferTerms.AdminImport.Toast.ImportSuccess", {
                        count: resp.data.data.createdCount,
                    }) as string,
                );
                onImported();
            }
        } catch (err: any) {
            const tooLarge = payloadTooLargeDetailsFromError(err);
            if (tooLarge) {
                setFileError(
                    t("ImportData.PayloadTooLarge", {
                        payloadSize: formatPayloadMegabytes(tooLarge.payloadBytes, i18n.language),
                        limitSize: formatPayloadMegabytes(tooLarge.limitBytes, i18n.language),
                    }),
                );
                setResult(null);
                return;
            }

            const httpStatus = err?.response?.status;
            const responseData = err?.response?.data;

            if (httpStatus === 400 && responseData?.message === "ServerError.ImportValidationFailed") {
                setValidationErrors({
                    missingTransferees: responseData.data?.missingTransferees ?? [],
                    missingIsbns: responseData.data?.missingIsbns ?? [],
                    rowErrors: responseData.data?.rowErrors ?? [],
                });
                setResult(null);
                return;
            }

            if (httpStatus === 400 && responseData?.message === "ServerError.TooManyRows") {
                setFileError(
                    t("TransferTerms.AdminImport.TooManyRowsExplanation", {
                        actual: responseData.data?.actual ?? "?",
                        limit: responseData.data?.limit ?? MAX_ROWS_PER_IMPORT,
                    }) as string,
                );
                setResult(null);
                return;
            }

            const rawMessage =
                responseData?.message ?? err?.message ?? t("TransferTerms.AdminImport.Toast.ImportFailed");
            const message = Array.isArray(rawMessage) ? rawMessage.join(" · ") : String(rawMessage);
            if (httpStatus === 400) {
                setFileError(message);
                setResult(null);
            } else {
                toast.error(message);
            }
        } finally {
            setLoading(false);
        }
    };

    const renderColumnsTable = (
        columns: ReadonlyArray<{ header: string; descriptionKey: string; example: string }>,
    ) => (
        <Table size="small" sx={{ fontFamily: "Poppins, sans-serif" }}>
            <TableHead>
                <TableRow>
                    <TableCell sx={{ fontFamily: "inherit", fontWeight: 500 }}>
                        {t("TransferTerms.AdminImport.Table.Header")}
                    </TableCell>
                    <TableCell sx={{ fontFamily: "inherit", fontWeight: 500 }}>
                        {t("TransferTerms.AdminImport.Table.Description")}
                    </TableCell>
                    <TableCell sx={{ fontFamily: "inherit", fontWeight: 500 }}>
                        {t("TransferTerms.AdminImport.Table.Example")}
                    </TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {columns.map((c) => (
                    <TableRow key={c.header}>
                        <TableCell sx={{ fontFamily: "inherit" }}>
                            <code>{c.header}</code>
                        </TableCell>
                        <TableCell sx={{ fontFamily: "inherit" }}>
                            {t(`TransferTerms.AdminImport.Columns.${c.descriptionKey}`)}
                        </TableCell>
                        <TableCell sx={{ fontFamily: "inherit" }}>
                            <code>{c.example}</code>
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );

    return (
        <ModalBase
            open={open}
            onClose={resetAndClose}
            modalTitle={t("TransferTerms.AdminImport.Title")}
            widthTransition={{ width: "800px", active: true }}
            maxHeight="90vh"
            contentStyle={{ fontFamily: "Poppins, sans-serif", overflowY: "auto", maxHeight: "calc(90vh - 100px)" }}
        >
            <Stack spacing={3} sx={{ fontFamily: "Poppins, sans-serif" }}>
                <div>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontFamily: "inherit", fontWeight: 500 }}>
                        <Trans
                            i18nKey="TransferTerms.AdminImport.RequiredColumnsTitle"
                            components={{ code: <code /> }}
                        />
                    </Typography>
                    {renderColumnsTable(REQUIRED_COLUMNS)}
                </div>

                <div>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontFamily: "inherit", fontWeight: 500 }}>
                        {t("TransferTerms.AdminImport.OptionalColumnsTitle")}
                    </Typography>
                    {renderColumnsTable(OPTIONAL_COLUMNS)}
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 1, display: "block", fontFamily: "inherit" }}
                    >
                        {t("TransferTerms.AdminImport.HeaderMatchingNote")}
                    </Typography>
                </div>

                <div>
                    <StyledButton buttonType="outlined" onClick={() => fileInputRef.current?.click()} fullWidth>
                        {file
                            ? t("TransferTerms.AdminImport.SelectedFile", { name: file.name })
                            : t("TransferTerms.AdminImport.ChooseFile")}
                    </StyledButton>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv,text/csv"
                        hidden
                        onChange={(e) => {
                            setFile(e.target.files?.[0] ?? null);
                            setResult(null);
                            setFileError(null);
                            setValidationErrors(null);
                        }}
                    />
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 0.5, display: "block", fontFamily: "inherit" }}
                    >
                        {t("TransferTerms.AdminImport.RowLimitNote", { limit: MAX_ROWS_PER_IMPORT })}
                    </Typography>
                </div>

                {fileError && (
                    <Alert severity="error" onClose={() => setFileError(null)} sx={{ fontFamily: "inherit" }}>
                        <Typography variant="subtitle2" sx={{ mb: 0.5, fontFamily: "inherit", fontWeight: 500 }}>
                            {t("TransferTerms.AdminImport.FileRejectedTitle")}
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", fontFamily: "inherit" }}>
                            {fileError}
                        </Typography>
                    </Alert>
                )}

                <div>
                    <Typography variant="subtitle2" sx={{ fontFamily: "inherit", fontWeight: 500 }}>
                        {t("TransferTerms.AdminImport.DefaultsSectionTitle")}
                    </Typography>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mb: 2, display: "block", fontFamily: "inherit" }}
                    >
                        {t("TransferTerms.AdminImport.DefaultsSectionHelper")}
                    </Typography>

                    <Stack spacing={3} sx={{ mt: 1 }}>
                        <div>
                            <Typography variant="caption" sx={{ mb: 0.5, display: "block", fontFamily: "inherit" }}>
                                {t("TransferTerms.AdminImport.StatusLabel")}
                            </Typography>
                            <StyledSelect
                                fullWidth
                                value={status}
                                onChange={(e) => setStatus(Number(e.target.value) as ETransferTermStatus)}
                            >
                                {STATUS_OPTION_KEYS.map((opt) => (
                                    <StyledMenuItem key={opt.value} value={opt.value}>
                                        {t(`TransferTerms.AdminImport.Status.${opt.key}`)}
                                    </StyledMenuItem>
                                ))}
                            </StyledSelect>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ mt: 0.5, display: "block", fontFamily: "inherit" }}
                            >
                                {t("TransferTerms.AdminImport.StatusHelper")}
                            </Typography>
                        </div>

                        <div>
                            <Typography variant="caption" sx={{ mb: 0.5, display: "block", fontFamily: "inherit" }}>
                                {t("TransferTerms.AdminImport.TrackingStartLabel")}
                            </Typography>
                            <StyledSelect
                                fullWidth
                                value={trackingStart}
                                onChange={(e) => setTrackingStart(e.target.value as TrackingStart)}
                            >
                                {TRACKING_START_OPTION_KEYS.map((opt) => (
                                    <StyledMenuItem key={opt.value} value={opt.value}>
                                        {t(`TransferTerms.AdminImport.TrackingStart.${opt.key}`)}
                                    </StyledMenuItem>
                                ))}
                            </StyledSelect>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ mt: 0.5, display: "block", fontFamily: "inherit" }}
                            >
                                {t("TransferTerms.AdminImport.TrackingStartHelper")}
                            </Typography>
                        </div>

                        <StyledTextField
                            label={t("TransferTerms.AdminImport.CountryCodesLabel")}
                            value={countryCodes}
                            onChange={(e) => setCountryCodes(e.target.value)}
                            placeholder="FR, BE, CA"
                            helperText={t("TransferTerms.AdminImport.CountryCodesHelper")}
                            fullWidth
                        />
                    </Stack>
                </div>

                {validationErrors && (
                    <Alert severity="error" onClose={() => setValidationErrors(null)} sx={{ fontFamily: "inherit" }}>
                        <Typography variant="subtitle2" sx={{ mb: 0.5, fontFamily: "inherit", fontWeight: 500 }}>
                            {t("TransferTerms.AdminImport.ValidationErrors.Title")}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1, fontFamily: "inherit" }}>
                            {t("TransferTerms.AdminImport.ValidationErrors.Explanation")}
                        </Typography>

                        {validationErrors.missingTransferees.length > 0 && (
                            <>
                                <Typography variant="body2" sx={{ fontWeight: 600, mt: 1, fontFamily: "inherit" }}>
                                    {t("TransferTerms.AdminImport.ValidationErrors.MissingTransferees", {
                                        count: validationErrors.missingTransferees.length,
                                    })}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    component="ul"
                                    sx={{ pl: 3, mt: 0.5, fontFamily: "inherit" }}
                                >
                                    {validationErrors.missingTransferees.map((name) => (
                                        <li key={name}>{name}</li>
                                    ))}
                                </Typography>
                            </>
                        )}
                        {validationErrors.missingIsbns.length > 0 && (
                            <>
                                <Typography variant="body2" sx={{ fontWeight: 600, mt: 1, fontFamily: "inherit" }}>
                                    {t("TransferTerms.AdminImport.ValidationErrors.MissingIsbns", {
                                        count: validationErrors.missingIsbns.length,
                                    })}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    component="ul"
                                    sx={{ pl: 3, mt: 0.5, fontFamily: "inherit" }}
                                >
                                    {validationErrors.missingIsbns.map((isbn) => (
                                        <li key={isbn}>
                                            <code>{isbn}</code>
                                        </li>
                                    ))}
                                </Typography>
                            </>
                        )}
                        {validationErrors.rowErrors.length > 0 && (
                            <>
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, mt: 1, mb: 1, fontFamily: "inherit" }}
                                >
                                    {t("TransferTerms.AdminImport.ValidationErrors.RowErrors", {
                                        count: validationErrors.rowErrors.length,
                                    })}
                                </Typography>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontFamily: "inherit", fontWeight: 500 }}>
                                                {t("TransferTerms.AdminImport.Table.Row")}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily: "inherit", fontWeight: 500 }}>
                                                {t("TransferTerms.AdminImport.Table.ISBN")}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily: "inherit", fontWeight: 500 }}>
                                                {t("TransferTerms.AdminImport.Table.Transferee")}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily: "inherit", fontWeight: 500 }}>
                                                {t("TransferTerms.AdminImport.Table.Reason")}
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {validationErrors.rowErrors.map((row, i) => (
                                            <TableRow key={i}>
                                                <TableCell sx={{ fontFamily: "inherit" }}>{row.row}</TableCell>
                                                <TableCell sx={{ fontFamily: "inherit" }}>{row.ean13 ?? "-"}</TableCell>
                                                <TableCell sx={{ fontFamily: "inherit" }}>
                                                    {row.transferee ?? "-"}
                                                </TableCell>
                                                <TableCell sx={{ fontFamily: "inherit" }}>{row.reason}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </>
                        )}
                    </Alert>
                )}

                {result && (
                    <Alert severity="success" sx={{ fontFamily: "inherit" }}>
                        {t("TransferTerms.AdminImport.Result.ImportSuccess", {
                            count: result.createdCount,
                        })}
                        {(result.deduplicatedCount ?? 0) > 0 &&
                            " " +
                                t("TransferTerms.AdminImport.Result.DeduplicatedSuffix", {
                                    count: result.deduplicatedCount,
                                })}
                    </Alert>
                )}

                <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", marginTop: "0.5rem" }}>
                    <StyledButton buttonType="outlined" onClick={resetAndClose} disabled={loading}>
                        {t("TransferTerms.AdminImport.Actions.Close")}
                    </StyledButton>
                    <StyledButton buttonType="primary" onClick={submit} disabled={!file} loading={loading}>
                        {t("TransferTerms.AdminImport.Actions.Import")}
                    </StyledButton>
                </div>
            </Stack>
        </ModalBase>
    );
};

export default AdminImportModal;
